import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toCustomerFacingErrorMessage } from '../errors/toCustomerFacingErrorMessage.js';
import { Button } from './Button.js';
import { Card } from './Card.js';
import { Loader } from './Loader.js';
import { Skeleton } from './Skeleton.js';

export type ScreenStateVariant =
  | 'loading'
  | 'error'
  | 'empty'
  | 'skeleton'
  | 'noResults';

export interface ScreenStateProps {
  readonly variant: ScreenStateVariant;
  readonly message?: string;
  readonly title?: string;
  readonly onRetry?: () => void;
  readonly action?: ReactNode;
  /** Custom skeleton body; when omitted, three pulse rows render. */
  readonly children?: ReactNode;
  readonly testId?: string;
}

const SKELETON_ROW_KEYS = ['sk-row-1', 'sk-row-2', 'sk-row-3'] as const;

export function ScreenState({
  variant,
  message,
  title,
  onRetry,
  action,
  children,
  testId,
}: ScreenStateProps): JSX.Element {
  const { t } = useTranslation('customer');

  if (variant === 'skeleton') {
    return (
      <Card padded>
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          data-testid={testId ?? 'screen-state-skeleton'}
          data-variant="skeleton"
        >
          {children ?? (
            <div className="flex flex-col gap-3">
              {SKELETON_ROW_KEYS.map((key, index) => (
                <Skeleton
                  key={key}
                  className={['h-4', index === SKELETON_ROW_KEYS.length - 1 ? 'w-2/3' : 'w-full'].join(
                    ' ',
                  )}
                  testId={`screen-state-skeleton-${key}`}
                />
              ))}
            </div>
          )}
        </div>
      </Card>
    );
  }

  if (variant === 'loading') {
    const resolvedMessage = message ?? t('shared.loading');
    return (
      <Card padded>
        <div
          className="flex items-center gap-3"
          role="status"
          aria-live="polite"
          aria-busy="true"
          data-testid={testId ?? 'screen-state-loading'}
          data-variant="loading"
        >
          <Loader size="sm" label={resolvedMessage} />
          <p className="text-sm text-[var(--color-on-surface-muted)]">{resolvedMessage}</p>
        </div>
      </Card>
    );
  }

  if (variant === 'error') {
    const publicFallback = t('shared.unexpectedError');
    const displayMessage =
      message === undefined || message.trim().length === 0
        ? publicFallback
        : toCustomerFacingErrorMessage(message, { fallback: publicFallback });
    return (
      <Card padded>
        <div
          data-testid={testId ?? 'screen-state-error'}
          data-variant="error"
        >
          <p className="text-sm text-[var(--color-danger)]" role="alert">
            {displayMessage}
          </p>
          {onRetry ? (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button type="button" intent="primary" size="md" onClick={onRetry}>
                {t('shared.retry')}
              </Button>
            </div>
          ) : null}
          {action ? <div className="mt-3">{action}</div> : null}
        </div>
      </Card>
    );
  }

  if (variant === 'noResults') {
    const publicFallback = t('shared.unexpectedError');
    const displayMessage =
      message === undefined || message.trim().length === 0
        ? undefined
        : toCustomerFacingErrorMessage(message, { fallback: publicFallback });
    return (
      <Card padded>
        <div
          className="flex flex-col gap-2"
          data-testid={testId ?? 'screen-state-no-results'}
          data-variant="noResults"
        >
          {title ? (
            <p className="text-sm font-medium text-[var(--color-on-surface)]">{title}</p>
          ) : null}
          {displayMessage !== undefined ? (
            <p className="text-sm text-[var(--color-on-surface-muted)]">{displayMessage}</p>
          ) : null}
          {action ? <div className="mt-2">{action}</div> : null}
        </div>
      </Card>
    );
  }

  const publicFallback = t('shared.unexpectedError');
  const displayEmptyMessage =
    message === undefined || message.trim().length === 0
      ? undefined
      : toCustomerFacingErrorMessage(message, { fallback: publicFallback });
  return (
    <Card padded>
      <div
        className="flex flex-col gap-2"
        data-testid={testId ?? 'screen-state-empty'}
        data-variant="empty"
      >
        {title ? (
          <h2 className="text-base font-semibold text-[var(--color-on-surface)]">{title}</h2>
        ) : null}
        {displayEmptyMessage !== undefined ? (
          <p className="text-sm text-[var(--color-on-surface-muted)]">{displayEmptyMessage}</p>
        ) : null}
        {action ? <div className="mt-2">{action}</div> : null}
      </div>
    </Card>
  );
}
